import LineRoomUpgradeIcon from 'components/Luxkit/Icons/line/LineRoomUpgradeIcon'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxLoyaltyTierCallOutBanner from 'luxLoyalty/components/LuxLoyaltyTierCallOutBanner'
import getLuxLoyaltyDisplayTier from 'luxLoyalty/selectors/getLuxLoyaltyDisplayTier'
import { getIsLuxLoyaltyEnabled } from 'luxLoyalty/selectors/luxLoyaltyFeatureToggles'
import React, { useCallback, useContext, useMemo } from 'react'
import OfferPageLuxLoyaltyRoomUpgradeCallOutBannerSwitch from './OfferPageLuxLoyaltyRoomUpgradeCallOutBannerSwitch'
import TextLink from 'components/Luxkit/TextLink'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import AccommodationRoomDetailsModal from './Accommodation/AccommodationRoomDetailsModal'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { isReservationAvailableForRoom } from 'lib/checkout/cartReservationUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'

const SubtitleTextLink = styled(TextLink)`
  color: var(--alt-contrast) !important; // @arminmon to fix TextLink colours;
`

interface Props {
  package: App.Package;
  durationPackageOptions: Array<OfferPackageOptionView>;
  rooms: Array<App.Occupants> | undefined;
  offer: App.Offer | undefined;
}

function OfferPageLuxLoyaltyRoomUpgradeCallOutBanner({
  package: pkg,
  durationPackageOptions,
  rooms = initialRooms,
  offer,
}: Props) {
  const tier = useAppSelector(getLuxLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)

  const targetUpgradePackage = useMemo<App.Package | undefined>(() => {
    if (!pkg.luxLoyalty.targetUpgradePackageUniqueKey) return undefined
    return offer?.packages.find(p => {
      return p.uniqueKey === pkg.luxLoyalty.targetUpgradePackageUniqueKey
    })
  }, [offer?.packages, pkg.luxLoyalty.targetUpgradePackageUniqueKey])

  const shouldShowUpgradeBanner = useMemo<boolean>(() => {
    if (!targetUpgradePackage || !offer || !rooms) return false
    // Target has invalid duration selected
    if (!durationPackageOptions.find(option => option.package.id === targetUpgradePackage.id)) return false
    // Target has invalid room capacity selected
    if (!rooms.every(room => isReservationAvailableForRoom(room, targetUpgradePackage.roomRate?.capacities ?? [], offer))) return false
    return true
  }, [durationPackageOptions, offer, rooms, targetUpgradePackage])

  const showTargetUpgradeRoomDetails = useCallback(() => {
    if (!targetUpgradePackage || !isHotelPackage(targetUpgradePackage)) return
    showModal(<AccommodationRoomDetailsModal
      roomPackage={targetUpgradePackage}
    />)
  }, [showModal, targetUpgradePackage])

  if (!shouldShowUpgradeBanner) return null

  return <LuxLoyaltyTierCallOutBanner
    tier={tier}
    end={<OfferPageLuxLoyaltyRoomUpgradeCallOutBannerSwitch
      package={pkg}
    />}
    icon={<LineRoomUpgradeIcon />}
    subtitle={<SubtitleTextLink onClick={showTargetUpgradeRoomDetails}>View room details</SubtitleTextLink>}
  >
    Apply your free room upgrade to {targetUpgradePackage!.name}
  </LuxLoyaltyTierCallOutBanner>
}

export default withMountGuard(() => useAppSelector(getIsLuxLoyaltyEnabled))(
  withOfferPageAndReduxState((state, appState) => ({
    durationPackageOptions: getDurationPackageOptions(state, appState),
    rooms: state.rooms,
    offer: state.offer,
  }))(OfferPageLuxLoyaltyRoomUpgradeCallOutBanner),
)
